import './error-boundary.scss';

import React, { type ErrorInfo, type JSX, type ReactNode } from 'react';
import { useLocation } from 'react-router';

import { isChunkLoadError, reloadOnceForStaleBuild } from '../lib/chunk-reload';

interface ErrorBoundaryProps {
	children?: ReactNode;
	fallBackUI?: JSX.Element;
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
	resetKey?: string;
	currentPath?: string;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
	errorId: string;
	occurredAt: string;
}

const createErrorId = () =>
	`ERR-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;

const getDefaultState = (): ErrorBoundaryState => ({
	hasError: false,
	error: null,
	errorId: '',
	occurredAt: '',
});

interface IDefaultFallback {
	error: Error | null;
	errorId: string;
	occurredAt: string;
	currentPath?: string;
	onTryAgain: () => void;
	onReload: () => void;
	onGoHome: () => void;
}

const StaleBuildFallbackUI = ({ onReload }: Pick<IDefaultFallback, 'onReload'>) => (
	<div
		className='app-error-boundary'
		role='alert'
		aria-live='assertive'
	>
		<div className='app-error-boundary__panel'>
			<h1>A new version of iElect is available</h1>
			<p className='app-error-boundary__subtitle'>This page was opened before the latest update. Reload to continue.</p>

			<div className='app-error-boundary__actions'>
				<button
					type='button'
					className='app-error-boundary__btn app-error-boundary__btn--primary'
					onClick={onReload}
				>
					Reload Page
				</button>
			</div>
		</div>
	</div>
);

const DefaultFallbackUI = ({ error, errorId, occurredAt, currentPath, onTryAgain, onReload, onGoHome }: IDefaultFallback) => (
	<div
		className='app-error-boundary'
		role='alert'
		aria-live='assertive'
	>
		<div className='app-error-boundary__panel'>
			<p className='app-error-boundary__eyebrow'>Application Error</p>
			<h1>We hit an unexpected issue</h1>
			<p className='app-error-boundary__subtitle'>
				The page crashed while loading this view. You can retry immediately or reload the application.
			</p>

			<div className='app-error-boundary__meta'>
				<span>
					<strong>Error ID:</strong> {errorId || 'N/A'}
				</span>
				<span>
					<strong>Time:</strong> {occurredAt ? new Date(occurredAt).toLocaleString() : 'N/A'}
				</span>
				<span>
					<strong>Path:</strong> {currentPath || window.location.pathname}
				</span>
			</div>

			<div className='app-error-boundary__actions'>
				<button
					type='button'
					className='app-error-boundary__btn app-error-boundary__btn--primary'
					onClick={onTryAgain}
				>
					Try Again
				</button>
				<button
					type='button'
					className='app-error-boundary__btn'
					onClick={onReload}
				>
					Reload Page
				</button>
				<button
					type='button'
					className='app-error-boundary__btn'
					onClick={onGoHome}
				>
					Go To Dashboard
				</button>
			</div>

			{import.meta.env.DEV && (
				<details className='app-error-boundary__details'>
					<summary>Developer details</summary>
					<pre>{error?.stack || error?.message || 'No error details available'}</pre>
				</details>
			)}
		</div>
	</div>
);

class ErrorBoundaryClass extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = getDefaultState();
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return {
			hasError: true,
			error,
			errorId: createErrorId(),
			occurredAt: new Date().toISOString(),
		};
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		console.error('ErrorBoundary caught an error:', error, errorInfo);
		this.props.onError?.(error, errorInfo);
		if (isChunkLoadError(error)) reloadOnceForStaleBuild();
	}

	componentDidUpdate(prevProps: Readonly<ErrorBoundaryProps>): void {
		if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
			this.setState(getDefaultState());
		}
	}

	onTryAgain = () => {
		this.setState(getDefaultState());
	};

	onReload = () => {
		window.location.reload();
	};

	onGoHome = () => {
		window.location.assign('/admin');
	};

	render(): ReactNode {
		if (this.state.hasError) {
			// Shown only if the automatic reload already ran (or is in flight) and the chunk still failed.
			if (!this.props.fallBackUI && isChunkLoadError(this.state.error)) {
				return <StaleBuildFallbackUI onReload={this.onReload} />;
			}

			return (
				this.props.fallBackUI ?? (
					<DefaultFallbackUI
						error={this.state.error}
						errorId={this.state.errorId}
						occurredAt={this.state.occurredAt}
						currentPath={this.props.currentPath}
						onTryAgain={this.onTryAgain}
						onReload={this.onReload}
						onGoHome={this.onGoHome}
					/>
				)
			);
		}

		return this.props.children;
	}
}

const ErrorBoundary = ({ children, ...props }: ErrorBoundaryProps) => {
	const location = useLocation();

	return (
		<ErrorBoundaryClass
			{...props}
			resetKey={location.pathname}
			currentPath={location.pathname}
		>
			{children}
		</ErrorBoundaryClass>
	);
};

export default ErrorBoundary;
